import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Pagination,
  IconButton,
  Tabs,
  Tab,
  Fade,
} from "@mui/material";
import {
  People as PeopleIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  Delete as DeleteIcon,
  VideoCall as VideoCallIcon,
  Bookmark as BookmarkIcon,
  AccessTime as AccessTimeIcon,
} from "@mui/icons-material";
import { useHistory, useLocation } from "react-router-dom";
import { styled, alpha, keyframes } from "@mui/material/styles";
import useApp from "../../store/contexts/AppContext";
import { ROUTES } from "../../routes/User/paths";
import Header from "../../components/Header";

// Helper function to format time remaining for ROOM expiration
const getTimeRemaining = (expirationTime) => {
  const now = new Date();
  const expiry = new Date(expirationTime);
  const diffMs = expiry - now;

  if (diffMs <= 0) return null;

  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(
    (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
  );
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (diffDays > 0) {
    return `${diffDays}d ${diffHours}h`;
  } else if (diffHours > 0) {
    return `${diffHours}h ${diffMinutes}m`;
  } else {
    return `${diffMinutes}m`;
  }
};

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#0a0a0a" : "#f8f9fa",
  minHeight: "100vh",
  paddingBottom: theme.spacing(8),
  position: "relative",
  "&::before": {
    content: '""',
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    height: "400px",
    background: theme.palette.mode === "dark"
      ? `radial-gradient(ellipse at top, ${alpha("#d44401", 0.15)}, transparent)`
      : `radial-gradient(ellipse at top, ${alpha("#d44402", 0.12)}, ${alpha("#1976d2", 0.08)}, transparent)`,
    pointerEvents: "none",
    zIndex: 0,
  },
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  position: "relative",
  zIndex: 1,
  paddingTop: theme.spacing(4),
  animation: `${fadeInUp} 0.6s cubic-bezier(0.4, 0, 0.2, 1)`,
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  borderBottom: `2px solid ${theme.palette.primary.main}`,
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: "2rem",
  fontWeight: 900,
  background: theme.palette.mode === "dark"
    ? `linear-gradient(135deg, ${theme.palette.common.white}, ${alpha(theme.palette.common.white, 0.7)})`
    : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  marginBottom: theme.spacing(1),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  color: "text.secondary",
  fontSize: "0.95rem",
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  "& .MuiTabs-indicator": {
    backgroundColor: theme.palette.primary.main,
    height: 3,
    borderRadius: 3,
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  color: "text.secondary",
  fontWeight: 600,
  fontSize: "0.9rem",
  textTransform: "none",
  minHeight: 48,
  transition: "all 0.3s ease",
  "&:hover": {
    color: "text.primary",
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  },
  "&.Mui-selected": {
    color: theme.palette.primary.main,
  },
}));

const RoomCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.02)
    : alpha(theme.palette.common.black, 0.02),
  border: theme.palette.mode === "dark"
    ? `1px solid ${alpha(theme.palette.common.white, 0.08)}`
    : `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
  borderRadius: 16,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  overflow: "hidden",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    transform: "translateY(-8px)",
    boxShadow: theme.palette.mode === "dark"
      ? `0 12px 32px ${alpha(theme.palette.common.black, 0.4)}`
      : `0 12px 32px ${alpha(theme.palette.common.black, 0.1)}`,
    borderColor: alpha(theme.palette.primary.main, 0.3),
  },
}));

const InactiveCard = styled(RoomCard)(({ theme }) => ({
  opacity: 0.6,
  "&:hover": {
    transform: "none",
    boxShadow: "none",
  },
}));

const CardHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: theme.spacing(1.5),
}));

const RoomName = styled(Typography)(({ theme }) => ({
  color: "text.primary",
  fontWeight: 700,
  fontSize: "1.1rem",
  lineHeight: 1.3,
  flex: 1,
  paddingRight: theme.spacing(1),
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  color: "#ef4444",
  padding: theme.spacing(0.75),
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: alpha("#ef4444", 0.15),
    transform: "scale(1.1)",
  },
}));

const ChipsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(0.75),
  marginBottom: theme.spacing(1.5),
  flexWrap: "wrap",
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  fontSize: "0.7rem",
  fontWeight: 600,
  height: 24,
}));

const TagsText = styled(Typography)(({ theme }) => ({
  color: "text.secondary",
  fontSize: "0.85rem",
  marginBottom: theme.spacing(1.5),
  fontStyle: "italic",
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
}));

const MetaSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.75),
  marginTop: "auto",
  paddingTop: theme.spacing(1.5),
  borderTop: theme.palette.mode === "dark"
    ? `1px solid ${alpha(theme.palette.common.white, 0.08)}`
    : `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
}));

const MetaItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  color: "text.secondary",
  fontSize: "0.8rem",
}));

const JoinButton = styled(Button)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(1.25),
  borderRadius: 20,
  fontWeight: 700,
  fontSize: "0.9rem",
  textTransform: "none",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
}));

const ActiveJoinButton = styled(JoinButton)(({ theme }) => ({
  backgroundColor: "#d44401",
  color: "#ffffff",
  border: "2px solid #d44401",
  "&:hover": {
    backgroundColor: "transparent",
    color: "#d44401",
    transform: "translateY(-2px)",
    boxShadow: `0 4px 16px ${alpha("#d44401", 0.4)}`,
  },
}));

const DisabledButton = styled(JoinButton)(({ theme }) => ({
  backgroundColor: "#374151",
  color: "#6c7589",
  border: "2px solid #374151",
  cursor: "not-allowed",
  "&:hover": {
    backgroundColor: "#374151",
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: "center",
  padding: theme.spacing(8, 2),
  backgroundColor: theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.02)
    : alpha(theme.palette.common.black, 0.02),
  borderRadius: 16,
  border: theme.palette.mode === "dark"
    ? `1px solid ${alpha(theme.palette.common.white, 0.08)}`
    : `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(6),
}));

const BookmarkedRooms = () => {
  const history = useHistory();
  const location = useLocation();
  const { getUserBookmarkedRoomsApiCall, unbookmarkRoomApiCall } = useApp();

  const [bookmarkedRooms, setBookmarkedRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterType, setFilterType] = useState("all");
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Fetch bookmarked rooms - refreshes on page load, filter change, or manual trigger
  useEffect(() => {
    const fetchBookmarkedRooms = async () => {
      setLoading(true);
      const params = {
        pageNo: currentPage,
        pageSize: 12,
      };

      if (filterType !== "all") {
        params.type = filterType;
      }

      const result = await getUserBookmarkedRoomsApiCall(params);

      if (result.success) {
        setBookmarkedRooms(result.data.bookmarked_rooms);
        setTotalPages(result.data.pagination.totalPages);
      }
      setLoading(false);
    };

    fetchBookmarkedRooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filterType, refreshTrigger]);

  // Refresh data when navigating back to this page
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setRefreshTrigger((prev) => prev + 1);
      }
    };

    // Listen for visibility change (tab switching)
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Trigger refresh on component mount (includes browser back/forward)
    setRefreshTrigger((prev) => prev + 1);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [location]);

  const handleUnbookmark = async (e, roomId) => {
    e.stopPropagation();
    const result = await unbookmarkRoomApiCall(roomId);
    if (result.success) {
      // Optimistically update UI
      setBookmarkedRooms((prev) => prev.filter((room) => room.id !== roomId));

      // Refresh to ensure consistency
      setRefreshTrigger((prev) => prev + 1);
    }
  };

  const handleJoinRoom = (roomId) => {
    history.push(`${ROUTES.room}/${roomId}`);
  };

  const handleFilterChange = (event, newValue) => {
    setFilterType(newValue);
    setCurrentPage(1);
  };

  const getRoomStatusChip = (room) => {
    if (room.is_expired) {
      return (
        <StyledChip
          label="EXPIRED"
          size="small"
          sx={{ backgroundColor: "#ef4444", color: "#ffffff" }}
        />
      );
    }
    if (!room.is_active) {
      return (
        <StyledChip
          label="INACTIVE"
          size="small"
          sx={{ backgroundColor: "#6c7589", color: "#ffffff" }}
        />
      );
    }
    return (
      <StyledChip
        label="ACTIVE"
        size="small"
        sx={{ backgroundColor: "#10b981", color: "#ffffff" }}
      />
    );
  };

  return (
    <>
      <Header />
      <PageContainer>
        <ContentContainer maxWidth="lg">
          <HeaderSection>
            <PageTitle>
              <BookmarkIcon sx={{ fontSize: "2rem", color: "#d44401" }} />
              Bookmarked Rooms
            </PageTitle>
            <Subtitle>Quick access to your favorite chat rooms</Subtitle>
          </HeaderSection>

          <StyledTabs value={filterType} onChange={handleFilterChange}>
            <StyledTab label="All Rooms" value="all" />
            <StyledTab label="Group Rooms" value="group" />
            <StyledTab label="Direct Messages" value="single" />
          </StyledTabs>

          {loading ? (
            <LoadingContainer>
              <CircularProgress sx={{ color: "#d44401" }} />
            </LoadingContainer>
          ) : bookmarkedRooms.length === 0 ? (
            <Fade in timeout={400}>
              <EmptyState>
                <BookmarkIcon sx={{ fontSize: 60, color: "#6c7589", mb: 2 }} />
                <Typography variant="h6" sx={{ color: "#9296b7", mb: 1 }}>
                  No bookmarked rooms found
                </Typography>
                <Typography variant="body2" sx={{ color: "#6c7589" }}>
                  Bookmark rooms to quickly access them later!
                </Typography>
              </EmptyState>
            </Fade>
          ) : (
            <>
              <Grid container spacing={3}>
                {bookmarkedRooms.map((room, index) => {
                  const isInactive = room.is_expired || !room.is_active;
                  const CardComponent = isInactive ? InactiveCard : RoomCard;

                  return (
                    <Grid item xs={12} sm={6} md={4} key={room.id}>
                      <Fade
                        in
                        timeout={300}
                        style={{ transitionDelay: `${index * 50}ms` }}
                      >
                        <CardComponent>
                          <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                            <CardHeader>
                              <RoomName title={room.name || "Unnamed Room"}>
                                {room.name || "Unnamed Room"}
                              </RoomName>
                              <DeleteButton
                                size="small"
                                onClick={(e) => handleUnbookmark(e, room.id)}
                                title="Remove bookmark"
                              >
                                <DeleteIcon fontSize="small" />
                              </DeleteButton>
                            </CardHeader>

                            <ChipsContainer>
                              {getRoomStatusChip(room)}
                              <StyledChip
                                icon={
                                  room.status === "public" ? (
                                    <PublicIcon fontSize="small" />
                                  ) : (
                                    <LockIcon fontSize="small" />
                                  )
                                }
                                label={room.status?.toUpperCase() || "UNKNOWN"}
                                size="small"
                                sx={{
                                  backgroundColor: alpha("#2a3478", 0.2),
                                  color: "#2a3478",
                                  border: "1px solid #2a3478",
                                }}
                              />
                              <StyledChip
                                icon={<PeopleIcon fontSize="small" />}
                                label={room.type === "group" ? "GROUP" : "DM"}
                                size="small"
                                sx={{
                                  backgroundColor: alpha("#2a3478", 0.2),
                                  color: "#2a3478",
                                  border: "1px solid #2a3478",
                                }}
                              />
                            </ChipsContainer>

                            {room.tags && (
                              <TagsText title={room.tags}>
                                Tags: {room.tags}
                              </TagsText>
                            )}

                            <MetaSection>
                              <MetaItem>
                                <BookmarkIcon fontSize="small" />
                                <Typography
                                  component="span"
                                  sx={{ fontSize: "0.8rem" }}
                                >
                                  Bookmarked{" "}
                                  {new Date(
                                    room.bookmarked_at,
                                  ).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  })}
                                </Typography>
                              </MetaItem>
                              {room.expiration_time ? (
                                <MetaItem>
                                  <AccessTimeIcon fontSize="small" />
                                  {room.is_expired ? (
                                    <Typography
                                      component="span"
                                      sx={{
                                        color: "#ef4444",
                                        fontSize: "0.8rem",
                                        fontWeight: 600,
                                      }}
                                    >
                                      Room expired{" "}
                                      {new Date(
                                        room.expiration_time,
                                      ).toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </Typography>
                                  ) : (
                                    <Typography
                                      component="span"
                                      sx={{
                                        color: "#10b981",
                                        fontSize: "0.8rem",
                                        fontWeight: 600,
                                      }}
                                    >
                                      Expires in{" "}
                                      {getTimeRemaining(room.expiration_time) ||
                                        "< 1m"}{" "}
                                      <Typography
                                        component="span"
                                        sx={{
                                          color: "#6c7589",
                                          fontSize: "0.75rem",
                                          fontWeight: 400,
                                          ml: 0.5,
                                        }}
                                      >
                                        (
                                        {new Date(
                                          room.expiration_time,
                                        ).toLocaleDateString("en-US", {
                                          month: "short",
                                          day: "numeric",
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        })}
                                        )
                                      </Typography>
                                    </Typography>
                                  )}
                                </MetaItem>
                              ) : (
                                <MetaItem>
                                  <AccessTimeIcon fontSize="small" />
                                  <Typography
                                    component="span"
                                    sx={{
                                      color: "#9296b7",
                                      fontSize: "0.8rem",
                                    }}
                                  >
                                    No expiration
                                  </Typography>
                                </MetaItem>
                              )}
                            </MetaSection>
                          </CardContent>

                          <CardActions sx={{ p: 2, pt: 0 }}>
                            {isInactive ? (
                              <DisabledButton disabled>
                                {room.is_expired ? "Expired" : "Inactive"}
                              </DisabledButton>
                            ) : (
                              <ActiveJoinButton
                                startIcon={<VideoCallIcon />}
                                onClick={() => handleJoinRoom(room.id)}
                              >
                                Join Room
                              </ActiveJoinButton>
                            )}
                          </CardActions>
                        </CardComponent>
                      </Fade>
                    </Grid>
                  );
                })}
              </Grid>

              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={4}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(e, page) => setCurrentPage(page)}
                    sx={(theme) => ({
                      "& .MuiPaginationItem-root": {
                        color: "text.secondary",
                        fontWeight: 600,
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        },
                        "&.Mui-selected": {
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.common.white,
                        },
                      },
                    })}
                  />
                </Box>
              )}
            </>
          )}
        </ContentContainer>
      </PageContainer>
    </>
  );
};

export default BookmarkedRooms;
